import * as ng from 'angular';
import { IFactureXResult, IFacturesXService } from '../factures-x.types';

interface IErrorSummarySubEntry {
	value: string;
	count: number;
	errorCode?: string;
	linkedId?: number;
}

interface IErrorSummaryEntry {
	field: string;
	errorMessage: string;
	errorCode?: string;
	count: number;
	values: IErrorSummarySubEntry[];
}

export default class FacturesXErrorReportModalController {
	public results: IFactureXResult[] = [];
	public errorSummary: IErrorSummaryEntry[] = [];
	public hasErrors: boolean = false;
	public modalInstance: any;
	public resolve: { results: IFactureXResult[] };
	public isRegenerating: boolean = false;

	private $state: any;
	private FacturesXService: IFacturesXService;
	private $uibModal: ng.ui.bootstrap.IModalService;
	private $timeout: ng.ITimeoutService;

	/* @ngInject */
	constructor($state: any, FacturesXService: IFacturesXService, $uibModal: ng.ui.bootstrap.IModalService, $timeout: ng.ITimeoutService) {
		this.$state = $state;
		this.FacturesXService = FacturesXService;
		this.$uibModal = $uibModal;
		this.$timeout = $timeout;
	}

	public $onInit(): void {
		this.results = this.resolve.results;
		this.hasErrors = this.results.some((r) => !r.success);
		if (this.hasErrors) {
			this.errorSummary = this._buildErrorSummary();
		}
	}

	private _buildErrorSummary(): IErrorSummaryEntry[] {
		const grouped = new Map<string, { valueCounts: Map<string, { count: number; errorCode?: string; linkedId?: number }>; errorCode?: string }>();
		for (const r of this.results) {
			if (r.success || !r.validations) continue;
			for (const v of r.validations) {
				if (v.isCompliant) continue;
				const key = `${v.field}|||${v.errorMessage?.split(':')[0]}`;
				if (!grouped.has(key)) grouped.set(key, { valueCounts: new Map(), errorCode: undefined });
				const entry = grouped.get(key)!;
				entry.errorCode = entry.errorCode ?? v.errorCode;
				const valueKey = v.value && v.value !== 'N/A' ? v.value : '';
				const existing = entry.valueCounts.get(valueKey);
				entry.valueCounts.set(valueKey, {
					count: (existing?.count ?? 0) + 1,
					errorCode: v.errorCode ?? existing?.errorCode,
					linkedId: v.linkedId ?? existing?.linkedId
				});
			}
		}
		return Array.from(grouped.entries())
			.map(([key, { valueCounts, errorCode }]) => {
				const [field, errorMessage] = key.split('|||');
				const values: IErrorSummarySubEntry[] = Array.from(valueCounts.entries())
					.filter(([v]) => v !== '')
					.map(([value, { count, errorCode: ec, linkedId }]) => ({ value, count, errorCode: ec, linkedId }))
					.sort((a, b) => b.count - a.count);
				const count = Array.from(valueCounts.values()).reduce((s, c) => s + c.count, 0);
				return { field, errorMessage, errorCode, count, values };
			})
			.sort((a, b) => b.count - a.count);
	}

	public getLinkUrl(sub: IErrorSummarySubEntry): string | null {
		if (!sub.linkedId) return null;
		const code = sub.errorCode ?? '';
		if (code.endsWith('ERR_COUNTRY_INVALID')) return this.$state.href('pays.edit', { id: sub.linkedId });
		if (code.endsWith('ERR_CURRENCY_INVALID')) return this.$state.href('devises.edit', { id: sub.linkedId });
		if (code.endsWith('ERR_SELLER_MISSING') || code.endsWith('ERR_SELLER_ERROR'))
			return this.$state.href('sites.edit', { id: sub.linkedId, genre: 'commerciaux' });
		if (code.endsWith('ERR_SOCIETE_ERROR') || code.endsWith('ERR_SOCIETE_MISSING'))
			return this.$state.href('societes.edit', { id: sub.linkedId });
		if (code.endsWith('ERR_BUYER_MISSING') || code.endsWith('ERR_BUYER_ERROR'))
			return this.$state.href('clients.edit', { id: sub.linkedId, genre: 'clients' });
		if (code.endsWith('ERR_BUYER_DIVERS_ERROR')) return this.$state.href('clients-divers.edit', { id: sub.linkedId });
		return null;
	}

	public getFactureEditUrl(factureId: number): string {
		return this.$state.href('factures.detail', { id: factureId });
	}

	public async regenerate(): Promise<void> {
		const failedIds = this.results.filter((r) => !r.success && r.factureId).map((r) => r.factureId as number);
		if (failedIds.length === 0) return;

		this.isRegenerating = true;

		const generatePromise = this.FacturesXService.generateFactureX(failedIds);

		const progressModal = this.$uibModal.open({
			component: 'facturesXProgressModal',
			resolve: { factureIds: () => failedIds },
			size: 'md',
			backdrop: 'static',
			keyboard: false
		});

		let newResults: IFactureXResult[] = [];

		try {
			newResults = await generatePromise;
		} catch {
			// ignore, results stay empty
		} finally {
			// Laisse la barre de progression visible un court instant avant de traiter le résultat.
			// $timeout (et non setTimeout) pour rester dans le digest Angular.
			this.$timeout(() => {
				progressModal.close();
				this.results = this.results.map((r) => {
					if (!r.factureId) return r;
					const updated = newResults.find((nr) => nr.factureId === r.factureId);
					return updated ?? r;
				});
				this.hasErrors = this.results.some((r) => !r.success);
				this.errorSummary = this.hasErrors ? this._buildErrorSummary() : [];
				this.isRegenerating = false;
			}, 2000);
		}
	}

	public close(): void {
		this.modalInstance.close();
	}
}
